import { useState } from 'react';
import { MessageSquare, Send, Loader2, X } from 'lucide-react';
import { useAuth0Safe as useAuth0 } from '@/hooks/useAuth0Safe';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ContactSellerButton — opens a small modal that POSTs a message to the
 * listing owner. Requires login (Auth0 or local admin JWT). Audit-logged
 * server-side; admins can review every conversation.
 */
export default function ContactSellerButton({ listingType, listingId, ownerName }) {
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  async function send() {
    if (!body.trim()) return;
    if (!isAuthenticated) { loginWithRedirect(); return; }
    setSending(true);
    try {
      const token = await getAccessTokenSilently();
      const r = await fetch(`${API}/me/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ listing_type: listingType, listing_id: listingId, body: body.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Slanje neuspješno');
      toast.success('Poruka poslana! Vlasnik će dobiti obavijest na e-mail.');
      setOpen(false); setBody('');
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  }

  return (
    <>
      <button onClick={() => isAuthenticated ? setOpen(true) : loginWithRedirect()}
              className="w-full mt-3 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-lg inline-flex items-center justify-center gap-2"
              data-testid="contact-seller-btn">
        <MessageSquare size={14} /> Pošalji poruku oglašivaču
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="contact-seller-modal">
          <div onClick={e => e.stopPropagation()} className="bg-white dark:bg-gray-900 w-full max-w-md rounded-2xl p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-900 dark:text-white">Poruka {ownerName ? `za ${ownerName}` : 'oglašivaču'}</h3>
              <button onClick={() => setOpen(false)} className="text-gray-400"><X size={18} /></button>
            </div>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={5}
                      placeholder="Napišite svoj upit (do 2000 znakova)…"
                      maxLength={2000}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 dark:bg-gray-950 dark:text-white rounded text-sm"
                      data-testid="contact-seller-body" />
            <p className="text-[10px] text-gray-500 mt-1.5">Konverzacija se sprema za sigurnost svih korisnika i može biti pregledana od strane administratora u slučaju prijavljene prijevare.</p>
            <button onClick={send} disabled={sending || !body.trim()}
                    className="w-full mt-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="contact-seller-send">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Pošalji
            </button>
          </div>
        </div>
      )}
    </>
  );
}
